import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeHeader,
  WireframeContent,
  WireframeCard,
  WireframeButton,
  StatCard,
} from './WireframeLayout';
import { Download, Plus, MapPin, Users, DollarSign, Clock, AlertCircle, Home, X, Check, ChevronRight, ChevronLeft, Calendar, CreditCard, Phone, Building2, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Modal, Input, Select, TextArea, LoadingSpinner, SkeletonStats, Skeleton, EmptyState, useToast } from './WireframeUI';
import { TutorialStartButton } from './WireframeTutorial';
import { wireframeMockApi } from '../../services/wireframeMockApi';
import { getAllNRCCountries, getRegionsForCountry } from '../../data/situationData';

// FilterDropdown component
function FilterDropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => options.length > 0 && setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-3 py-1.5 min-w-[120px] cursor-pointer hover:border-gray-300"
      >
        <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[12px] font-semibold flex items-center justify-between gap-2">
          {value}{' '}
          <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform", isOpen && "rotate-180")} />
        </div>
      </div>

      {isOpen && options.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[160px] max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-50",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Date options for the dropdown
const dateOptions = [
  'Feb 2026',
  'Jan 2026',
  'Dec 2025',
  'Nov 2025',
  'Oct 2025',
  'Last 7 days',
  'Last 30 days',
  'Last 90 days',
];

interface LocationData {
  id: string;
  name: string;
  coordinates: [number, number];
  affected: number;
  severity: 'critical' | 'high' | 'moderate';
  demographics: {
    male: number;
    female: number;
    ages: { [key: string]: number };
    vulnerability: { femaleHeaded: number; childHeaded: number; elderlySingle: number; withDisability: number; chronicIllness: number };
    housing: { tents: number; temporary: number; damaged: number; adequate: number };
    personsPerRoom: number;
  };
}

const locationData: LocationData[] = [
  {
    id: 'fik',
    name: 'Fik',
    coordinates: [42.53, 7.77],
    affected: 6700,
    severity: 'high',
    demographics: {
      male: 3283,
      female: 3417,
      ages: { '0-5': 1005, '6-17': 2010, '18-59': 3216, '60+': 469 },
      vulnerability: { femaleHeaded: 87, childHeaded: 9, elderlySingle: 19, withDisability: 52, chronicIllness: 78 },
      housing: { tents: 123, temporary: 189, damaged: 98, adequate: 23 },
      personsPerRoom: 4.5,
    },
  },
  {
    id: 'jijiga',
    name: 'Jijiga',
    coordinates: [42.79, 9.35],
    affected: 13200,
    severity: 'critical',
    demographics: {
      male: 6468,
      female: 6732,
      ages: { '0-5': 1980, '6-17': 3960, '18-59': 6336, '60+': 924 },
      vulnerability: { femaleHeaded: 172, childHeaded: 18, elderlySingle: 38, withDisability: 103, chronicIllness: 154 },
      housing: { tents: 243, temporary: 372, damaged: 193, adequate: 45 },
      personsPerRoom: 5.2,
    },
  },
  {
    id: 'gode',
    name: 'Gode',
    coordinates: [43.45, 5.95],
    affected: 8400,
    severity: 'high',
    demographics: {
      male: 4116,
      female: 4284,
      ages: { '0-5': 1260, '6-17': 2520, '18-59': 4032, '60+': 588 },
      vulnerability: { femaleHeaded: 109, childHeaded: 11, elderlySingle: 24, withDisability: 66, chronicIllness: 98 },
      housing: { tents: 154, temporary: 236, damaged: 123, adequate: 29 },
      personsPerRoom: 4.8,
    },
  },
  {
    id: 'kebridehar',
    name: 'Kebridehar',
    coordinates: [44.2, 6.73],
    affected: 7100,
    severity: 'moderate',
    demographics: {
      male: 3479,
      female: 3621,
      ages: { '0-5': 1065, '6-17': 2130, '18-59': 3408, '60+': 497 },
      vulnerability: { femaleHeaded: 92, childHeaded: 9, elderlySingle: 20, withDisability: 55, chronicIllness: 83 },
      housing: { tents: 130, temporary: 200, damaged: 104, adequate: 24 },
      personsPerRoom: 4.3,
    },
  },
  {
    id: 'degehabur',
    name: 'Degehabur',
    coordinates: [43.56, 8.22],
    affected: 15300,
    severity: 'critical',
    demographics: {
      male: 7497,
      female: 7803,
      ages: { '0-5': 2295, '6-17': 4590, '18-59': 7344, '60+': 1071 },
      vulnerability: { femaleHeaded: 199, childHeaded: 21, elderlySingle: 44, withDisability: 119, chronicIllness: 179 },
      housing: { tents: 281, temporary: 431, damaged: 224, adequate: 52 },
      personsPerRoom: 5.5,
    },
  },
];

// ============================================================================
// NEW DISTRIBUTION WIZARD
// ============================================================================

interface DistributionWizardProps {
  open: boolean;
  onClose: () => void;
  locations: LocationData[];
  onComplete: (data: DistributionData) => void;
}

interface DistributionData {
  locations: string[];
  modality: string;
  amount: string;
  currency: string;
  frequency: string;
  startDate: string;
  endDate: string;
  verificationMethod: string;
  targetCriteria: string[];
  notes: string;
}

const WIZARD_STEPS = [
  { id: 1, title: 'Target Locations', description: 'Select distribution areas' },
  { id: 2, title: 'Transfer Details', description: 'Configure amount and modality' },
  { id: 3, title: 'Schedule & Criteria', description: 'Set timing and targeting' },
  { id: 4, title: 'Review & Confirm', description: 'Verify distribution plan' },
];

function NewDistributionWizard({ open, onClose, locations, onComplete }: DistributionWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<DistributionData>({
    locations: [],
    modality: '',
    amount: '',
    currency: 'ETB',
    frequency: 'one-time',
    startDate: '',
    endDate: '',
    verificationMethod: '',
    targetCriteria: [],
    notes: '',
  });

  const updateFormData = (updates: Partial<DistributionData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const toggleLocation = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.includes(locationId)
        ? prev.locations.filter(id => id !== locationId)
        : [...prev.locations, locationId],
    }));
  };

  const toggleCriteria = (criterion: string) => {
    setFormData(prev => ({
      ...prev,
      targetCriteria: prev.targetCriteria.includes(criterion)
        ? prev.targetCriteria.filter(c => c !== criterion)
        : [...prev.targetCriteria, criterion],
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.locations.length > 0;
      case 2:
        return formData.modality && formData.amount;
      case 3:
        return formData.startDate && formData.verificationMethod;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    onComplete(formData);
    setIsSubmitting(false);
    onClose();
    // Reset form
    setCurrentStep(1);
    setFormData({
      locations: [],
      modality: '',
      amount: '',
      currency: 'ETB',
      frequency: 'one-time',
      startDate: '',
      endDate: '',
      verificationMethod: '',
      targetCriteria: [],
      notes: '',
    });
  };

  const selectedLocations = locations.filter(l => formData.locations.includes(l.id));
  const totalAffected = selectedLocations.reduce((sum, l) => sum + l.affected, 0);
  const estimatedHouseholds = Math.round(totalAffected / 5);
  const estimatedBudget = estimatedHouseholds * (parseInt(formData.amount) || 0);

  const modalityOptions = [
    { value: 'mobile-money', label: 'Mobile Money Transfer' },
    { value: 'cash-in-hand', label: 'Cash-in-Hand Distribution' },
    { value: 'bank-transfer', label: 'Bank Account Transfer' },
    { value: 'voucher', label: 'E-Voucher / Paper Voucher' },
  ];

  const verificationOptions = [
    { value: 'biometric', label: 'Biometric Verification' },
    { value: 'id-card', label: 'ID Card + PIN' },
    { value: 'token', label: 'Token-Based Distribution' },
    { value: 'community', label: 'Community-Based Verification' },
  ];

  const targetingCriteria = [
    'Female-headed households',
    'Child-headed households',
    'Elderly single-person households',
    'Households with disabled members',
    'Households with chronic illness',
    'Inadequate housing (tents/temporary)',
    'High persons per room (>4)',
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Cash Distribution"
      subtitle={`Step ${currentStep} of 4: ${WIZARD_STEPS[currentStep - 1].title}`}
      size="lg"
    >
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 px-2">
        {WIZARD_STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-8 h-8 flex items-center justify-center text-sm font-medium border-2 transition-colors',
                  currentStep > step.id
                    ? 'bg-green-600 border-green-600 text-white'
                    : currentStep === step.id
                    ? 'bg-accent-500 border-accent-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                )}
              >
                {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
              </div>
              <div className="mt-2 text-center">
                <div className={cn('text-xs font-medium', currentStep >= step.id ? 'text-gray-900' : 'text-gray-400')}>
                  {step.title}
                </div>
              </div>
            </div>
            {index < WIZARD_STEPS.length - 1 && (
              <div
                className={cn(
                  'w-16 h-0.5 mx-2 mt-[-16px]',
                  currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]">
        {/* Step 1: Target Locations */}
        {currentStep === 1 && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Select the locations where cash assistance will be distributed. You can select multiple locations.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {locations.map(location => (
                <button
                  key={location.id}
                  onClick={() => toggleLocation(location.id)}
                  className={cn(
                    'p-4 text-left border transition-all',
                    formData.locations.includes(location.id)
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-sm">{location.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {location.affected.toLocaleString()} affected
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'text-[10px] font-semibold px-2 py-0.5 text-white uppercase',
                          location.severity === 'critical' ? 'bg-red-600' :
                          location.severity === 'high' ? 'bg-amber-500' : 'bg-amber-400'
                        )}
                      >
                        {location.severity}
                      </span>
                      {formData.locations.includes(location.id) && (
                        <Check className="w-5 h-5 text-accent-500" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {formData.locations.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 text-sm">
                <strong>{formData.locations.length}</strong> location(s) selected •{' '}
                <strong>{totalAffected.toLocaleString()}</strong> total affected •{' '}
                ~<strong>{estimatedHouseholds.toLocaleString()}</strong> estimated households
              </div>
            )}
          </div>
        )}

        {/* Step 2: Transfer Details */}
        {currentStep === 2 && (
          <div className="space-y-5">
            <p className="text-sm text-gray-600 mb-4">
              Configure the transfer modality and amount for this distribution.
            </p>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Transfer Modality
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {modalityOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => updateFormData({ modality: option.value })}
                    className={cn(
                      'p-4 text-left border transition-all flex items-center gap-3',
                      formData.modality === option.value
                        ? 'border-accent-500 bg-accent-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    {option.value === 'mobile-money' && <Phone className="w-5 h-5 text-gray-400" />}
                    {option.value === 'cash-in-hand' && <DollarSign className="w-5 h-5 text-gray-400" />}
                    {option.value === 'bank-transfer' && <Building2 className="w-5 h-5 text-gray-400" />}
                    {option.value === 'voucher' && <CreditCard className="w-5 h-5 text-gray-400" />}
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Amount per Household"
                value={formData.amount}
                onChange={(value) => updateFormData({ amount: value })}
                placeholder="e.g., 580"
                type="number"
                required
              />
              <Select
                label="Currency"
                value={formData.currency}
                onChange={(value) => updateFormData({ currency: value })}
                options={[
                  { value: 'ETB', label: 'Ethiopian Birr (ETB)' },
                  { value: 'USD', label: 'US Dollar (USD)' },
                ]}
              />
            </div>

            <Select
              label="Distribution Frequency"
              value={formData.frequency}
              onChange={(value) => updateFormData({ frequency: value })}
              options={[
                { value: 'one-time', label: 'One-time Distribution' },
                { value: 'monthly', label: 'Monthly (3 months)' },
                { value: 'bi-weekly', label: 'Bi-weekly (6 weeks)' },
              ]}
            />

            {formData.amount && (
              <div className="p-4 bg-blue-50 border-l-4 border-accent-500">
                <div className="text-xs font-semibold text-accent-500 uppercase mb-1">Estimated Budget</div>
                <div className="text-xl font-bold font-mono">
                  {estimatedBudget.toLocaleString()} {formData.currency}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Based on ~{estimatedHouseholds.toLocaleString()} households at {formData.amount} {formData.currency} each
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Schedule & Criteria */}
        {currentStep === 3 && (
          <div className="space-y-5">
            <p className="text-sm text-gray-600 mb-4">
              Set the distribution schedule and targeting criteria.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => updateFormData({ startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => updateFormData({ endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Verification Method <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {verificationOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => updateFormData({ verificationMethod: option.value })}
                    className={cn(
                      'p-3 text-left border transition-all text-sm',
                      formData.verificationMethod === option.value
                        ? 'border-accent-500 bg-accent-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Targeting Criteria (Optional)
              </label>
              <div className="space-y-2">
                {targetingCriteria.map(criterion => (
                  <label
                    key={criterion}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.targetCriteria.includes(criterion)}
                      onChange={() => toggleCriteria(criterion)}
                      className="w-4 h-4 border-gray-300 text-accent-500 focus:ring-accent-500"
                    />
                    <span className="text-sm">{criterion}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review & Confirm */}
        {currentStep === 4 && (
          <div className="space-y-5">
            <p className="text-sm text-gray-600 mb-4">
              Review the distribution plan before creating.
            </p>

            <div className="border border-gray-200 divide-y divide-gray-200">
              <div className="p-4">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Target Locations</div>
                <div className="flex flex-wrap gap-2">
                  {selectedLocations.map(loc => (
                    <span key={loc.id} className="px-2 py-1 bg-gray-100 text-sm font-medium">
                      {loc.name}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {totalAffected.toLocaleString()} affected • ~{estimatedHouseholds.toLocaleString()} households
                </div>
              </div>

              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Transfer Modality</div>
                  <div className="text-sm font-medium">
                    {modalityOptions.find(o => o.value === formData.modality)?.label}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Amount per HH</div>
                  <div className="text-sm font-medium font-mono">
                    {formData.amount} {formData.currency}
                  </div>
                </div>
              </div>

              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Distribution Period</div>
                  <div className="text-sm font-medium">
                    {formData.startDate} {formData.endDate ? `to ${formData.endDate}` : '(single day)'}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Verification</div>
                  <div className="text-sm font-medium">
                    {verificationOptions.find(o => o.value === formData.verificationMethod)?.label}
                  </div>
                </div>
              </div>

              {formData.targetCriteria.length > 0 && (
                <div className="p-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Targeting Criteria</div>
                  <div className="flex flex-wrap gap-2">
                    {formData.targetCriteria.map(c => (
                      <span key={c} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-4 bg-green-50">
                <div className="text-xs font-semibold text-green-700 uppercase mb-1">Total Estimated Budget</div>
                <div className="text-2xl font-bold font-mono text-green-700">
                  {estimatedBudget.toLocaleString()} {formData.currency}
                </div>
              </div>
            </div>

            <TextArea
              label="Additional Notes"
              value={formData.notes}
              onChange={(value) => updateFormData({ notes: value })}
              placeholder="Any additional instructions or notes for field coordinators..."
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={currentStep === 1 ? onClose : handleBack}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
        >
          {currentStep === 1 ? 'Cancel' : (
            <span className="flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Back
            </span>
          )}
        </button>

        {currentStep < 4 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className={cn(
              'px-4 py-2 text-sm font-medium flex items-center gap-1',
              canProceed()
                ? 'bg-accent-500 text-white hover:bg-accent-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="text-white" />
                Creating...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Create Distribution
              </>
            )}
          </button>
        )}
      </div>
    </Modal>
  );
}

export function WireframeCashAssistance() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'cash' }, '*');
    }
  }, [isEmbedded]);

  const [selectedLocation, setSelectedLocation] = useState<LocationData>(locationData[0]);
  const [activeTab, setActiveTab] = useState<'demographics' | 'market' | 'field'>('demographics');
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showDistributionWizard, setShowDistributionWizard] = useState(false);

  // Location and date filters
  const [selectedCountry, setSelectedCountry] = useState('Ethiopia');
  const [selectedRegion, setSelectedRegion] = useState('Somali');
  const [selectedDate, setSelectedDate] = useState('Feb 2026');

  const allCountries = getAllNRCCountries();
  const regionsList = getRegionsForCountry(selectedCountry);

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');
  };
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  // Fallback token for wireframe demos
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic2FtZnJvbnMiLCJhIjoiY20wcXprNHFmMDM5dDJpb3A3czEyYXU2OCJ9.z7lv5-q6CZW8Xq59kn1bYA';
  const { addToast } = useToast();

  // Simulate initial data loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleExportData = async () => {
    setIsExporting(true);
    await wireframeMockApi.exportToPdf('cash-assistance-data.pdf');
    setIsExporting(false);
    addToast({
      type: 'success',
      title: 'Export Complete',
      message: 'Cash assistance data exported successfully',
    });
  };

  const handleExportAssessment = async () => {
    setIsExporting(true);
    await wireframeMockApi.exportToPdf(`${selectedLocation.name.toLowerCase()}-cash-assessment.pdf`);
    setIsExporting(false);
    addToast({
      type: 'success',
      title: 'Assessment Exported',
      message: `${selectedLocation.name} cash assessment report downloaded`,
    });
  };

  const handleDistributionComplete = (data: DistributionData) => {
    const locationNames = locationData
      .filter(l => data.locations.includes(l.id))
      .map(l => l.name)
      .join(', ');
    addToast({
      type: 'success',
      title: 'Distribution Created',
      message: `New cash distribution created for ${locationNames}`,
    });
  };

  // Initialize map
  useEffect(() => {
    // Prevent double init
    if (mapRef.current) {
      return;
    }

    const container = document.getElementById('wireframe-cash-map');
    if (!container) {
      return;
    }

    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: 'wireframe-cash-map',
      style: 'mapbox://styles/mapbox/light-v11',
      center: [42.5, 7.5],
      zoom: 6,
    });

    mapRef.current = map;

    map.on('load', () => {
      const colors: Record<string, string> = {
        critical: '#DC2626',
        high: '#F59E0B',
        moderate: '#FBBF24',
      };

      locationData.forEach((location) => {
        const el = document.createElement('div');
        const color = colors[location.severity];

        el.style.cssText = `
          width: 40px;
          height: 40px;
          background: white;
          border: 3px solid ${color};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          font-family: Inter, sans-serif;
          cursor: pointer;
          color: ${color};
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          position: relative;
        `;
        el.innerHTML = location.affected >= 10000 ? `${Math.round(location.affected / 1000)}k` : location.affected.toString();

        // Create popup with location info
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: true, closeOnClick: false }).setHTML(`
          <div style="padding: 8px; font-family: Inter, sans-serif; min-width: 180px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${location.name}</div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Affected</span>
              <span style="font-weight: 600; color: ${color};">${location.affected.toLocaleString()}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;">
              <span style="color: #737373;">Severity</span>
              <span style="font-weight: 600; color: ${color}; text-transform: uppercase;">${location.severity}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;">
              <span style="color: #737373;">Est. Households</span>
              <span style="font-weight: 600;">~${Math.round(location.affected / 5).toLocaleString()}</span>
            </div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #E5E5E5; font-size: 11px; color: #6B7280;">
              Click for full demographics →
            </div>
          </div>
        `);

        // Handle popup open to update side panel
        popup.on('open', () => {
          setSelectedLocation(location);
        });

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(location.coordinates)
          .setPopup(popup)
          .addTo(map);

        markersRef.current.push(marker);
      });
    });

    // Don't clean up on unmount - let it persist
  }, []);

  const formatNumber = (num: number) => num.toLocaleString();

  const severityColors = {
    critical: 'bg-red-600',
    high: 'bg-amber-500',
    moderate: 'bg-amber-400',
  };

  return (
    <WireframeLayout embedded={isEmbedded}>
      {!isEmbedded && <WireframeHeader
        title="Cash Assistance Targeting & Distribution"
        subtitle="AI-powered beneficiary mapping and market analysis for emergency cash programming"
        breadcrumbs={[
          { label: 'CLEAR', href: '/wireframes' },
          { label: 'Cash Assistance' }
        ]}
        actions={
          <>
            <FilterDropdown
              label="Country"
              value={selectedCountry}
              options={allCountries}
              onChange={handleCountryChange}
            />
            <FilterDropdown
              label="Region"
              value={selectedRegion}
              options={regionsList}
              onChange={setSelectedRegion}
            />
            <FilterDropdown
              label="Date"
              value={selectedDate}
              options={dateOptions}
              onChange={setSelectedDate}
            />
            <TutorialStartButton tutorialId="cash" variant="icon" />
            <div data-tutorial="export-data">
              <WireframeButton
                icon={isExporting ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />}
                onClick={handleExportData}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Data'}
              </WireframeButton>
            </div>
            <div data-tutorial="new-distribution">
              <WireframeButton
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowDistributionWizard(true)}
              >
                New Distribution
              </WireframeButton>
            </div>
          </>
        }
      />}

      {/* Active Crisis Banner */}
      <div className="bg-amber-50 border-b border-amber-300 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-[18px] h-[18px] text-amber-700" />
          <span className="text-[13px] text-gray-900">
            <strong className="text-amber-700">Active Crisis:</strong> Cholera Outbreak - Somali Region • Jijiga, Kebridehar
          </span>
        </div>
        <span className="text-[11px] font-semibold px-2.5 py-1 bg-white border border-gray-200 text-gray-600">
          46h intervention window
        </span>
      </div>

      <WireframeContent>
        {/* Stats Row */}
        {isLoading ? (
          <SkeletonStats count={4} />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Affected Population" value="185,000" icon={<Users className="w-4 h-4" />} />
            <StatCard label="Cash Transfer/HH" value="580 Birr" icon={<DollarSign className="w-4 h-4" />} />
            <StatCard label="Eligible HH (AI)" value="5,000" valueColor="text-green-600" icon={<Clock className="w-4 h-4" />} />
            <StatCard label="Total Budget" value="2.9M Birr" icon={<DollarSign className="w-4 h-4" />} />
          </div>
        )}

        {/* Map Section Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-[18px] h-[18px] text-gray-900" />
            <h2 className="text-[15px] font-semibold text-gray-900">Cash Assistance Mapping & Assessment</h2>
          </div>
          <p className="text-xs text-gray-500">
            Click on map markers to view detailed population demographics, market analysis, and field coordinator inputs for cash/voucher feasibility
          </p>
        </div>

        {/* Map and Details Panel */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {/* Map Container */}
          <div className="w-full lg:flex-[0_0_65%] relative" data-tutorial="map">
            <WireframeCard noPadding className="overflow-hidden">
              {/* Map Legend */}
              <div className="absolute top-3 left-3 z-10 bg-white border border-gray-200 p-3">
                <div className="space-y-1.5 text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-red-600" />
                    <span>Critical Exposure</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-amber-500" />
                    <span>High Exposure</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-amber-400" />
                    <span>Moderate Exposure</span>
                  </div>
                </div>
              </div>
              <div
                ref={mapContainerRef}
                id="wireframe-cash-map"
                className="h-[500px] w-full"
                style={{ width: '100%', height: '500px' }}
              />
            </WireframeCard>
          </div>

          {/* Details Panel */}
          <div className="w-full lg:flex-[0_0_35%] min-w-0" data-tutorial="demographics">
            <WireframeCard className="h-[500px] overflow-y-auto" noPadding>
              {/* Panel Header */}
              <div className="sticky top-0 bg-white z-10 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="font-semibold text-sm">{selectedLocation.name}</span>
                </div>
                <button className="w-7 h-7 flex items-center justify-center border border-gray-200 hover:bg-gray-50">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Total Affected */}
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-[11px] text-gray-500 uppercase tracking-wide">Total Affected</span>
                  </div>
                  <div className="text-2xl font-bold font-mono">{formatNumber(selectedLocation.affected)}</div>
                </div>
                <span
                  className={cn(
                    'text-[10px] font-semibold px-2.5 py-1 text-white uppercase',
                    severityColors[selectedLocation.severity]
                  )}
                >
                  {selectedLocation.severity}
                </span>
              </div>

              {/* Export Button */}
              <div className="px-5 py-3 border-b border-gray-200">
                <WireframeButton
                  variant="primary"
                  className="w-full justify-center"
                  onClick={handleExportAssessment}
                  disabled={isExporting}
                  icon={isExporting ? <LoadingSpinner size="sm" className="text-white" /> : undefined}
                >
                  {isExporting ? 'Exporting...' : 'Export Cash Assessment Report (PDF)'}
                </WireframeButton>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {(['demographics', 'market', 'field'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    data-tutorial={tab === 'market' ? 'market-tab' : tab === 'field' ? 'field-tab' : undefined}
                    className={cn(
                      'flex-1 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors',
                      activeTab === tab
                        ? 'text-accent-500 border-accent-500'
                        : 'text-gray-500 border-transparent hover:text-gray-900'
                    )}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-5">
                {activeTab === 'demographics' && (
                  <>
                    {/* Gender Distribution */}
                    <div className="mb-5">
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase">Gender Distribution</span>
                      </div>
                      <div className="space-y-2">
                        <ProgressBar
                          label="Male"
                          value={selectedLocation.demographics.male}
                          total={selectedLocation.affected}
                        />
                        <ProgressBar
                          label="Female"
                          value={selectedLocation.demographics.female}
                          total={selectedLocation.affected}
                        />
                      </div>
                    </div>

                    {/* Age Distribution */}
                    <div className="mb-5">
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase">Age Distribution</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(selectedLocation.demographics.ages).map(([range, count]) => (
                          <div key={range} className="bg-gray-50 p-2.5 text-center">
                            <div className="text-[10px] text-gray-500 mb-1">{range} years</div>
                            <div className="text-sm font-semibold font-mono">{formatNumber(count)}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Vulnerability Indicators */}
                    <div className="mb-5">
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <AlertCircle className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase">Vulnerability Indicators (HH)</span>
                      </div>
                      <div className="text-xs divide-y divide-gray-200">
                        {Object.entries(selectedLocation.demographics.vulnerability).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-1.5">
                            <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                            <span className="font-mono font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Housing Conditions */}
                    <div className="mb-5">
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <Home className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase">Housing Conditions (HH)</span>
                      </div>
                      <div className="text-xs divide-y divide-gray-200">
                        {Object.entries(selectedLocation.demographics.housing).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-1.5">
                            <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                            <span className="font-mono font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Avg Persons/Room */}
                    <div className="bg-gray-50 p-3">
                      <div className="text-[11px] text-gray-500 mb-1">Avg. Persons/Room</div>
                      <div className="text-xl font-bold font-mono">{selectedLocation.demographics.personsPerRoom}</div>
                    </div>
                  </>
                )}

                {activeTab === 'market' && (
                  <>
                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Market Functionality</div>
                      <div className="flex items-center gap-2 p-3 bg-green-100 mb-2">
                        <span className="w-2 h-2 bg-green-600" />
                        <span className="text-[13px] font-medium text-green-600">Markets Operational</span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        3 of 4 major markets in the area are functioning normally. One market ({selectedLocation.name} Central) has reduced hours due to security concerns.
                      </p>
                    </div>

                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Vendor Availability</div>
                      <div className="text-xs divide-y divide-gray-200">
                        <div className="flex justify-between py-1.5">
                          <span>Food vendors</span>
                          <span className="font-mono font-medium text-green-600">12 active</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>NFI vendors</span>
                          <span className="font-mono font-medium text-green-600">8 active</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>Mobile money agents</span>
                          <span className="font-mono font-medium text-amber-600">3 active</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Price Stability</div>
                      <div className="text-xs divide-y divide-gray-200">
                        <div className="flex justify-between py-1.5">
                          <span>Staple foods</span>
                          <span className="text-amber-600">+12% vs baseline</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>Water (20L)</span>
                          <span className="text-red-600">+28% vs baseline</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>Transport</span>
                          <span className="text-green-600">+5% vs baseline</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-3 border-l-[3px] border-accent-500">
                      <div className="text-xs font-semibold text-accent-500 mb-1">Recommendation</div>
                      <div className="text-xs text-gray-600">
                        Cash transfer is viable. Consider voucher restrictions for water purchases due to price inflation.
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'field' && (
                  <>
                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Coordinator Notes</div>
                      <div className="bg-gray-50 p-3 text-xs text-gray-600 leading-relaxed">
                        "Community leaders have been consulted and are supportive of cash distribution. Women's groups prefer mobile money due to safety concerns with physical cash. Recommend morning distributions to avoid afternoon heat."
                        <div className="mt-2 text-[11px] text-gray-400">— Ahmed M., Field Coordinator, Dec 14</div>
                      </div>
                    </div>

                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Accessibility</div>
                      <div className="text-xs divide-y divide-gray-200">
                        <div className="flex justify-between py-1.5">
                          <span>Road access</span>
                          <span className="font-medium text-green-600">Good</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>Mobile network</span>
                          <span className="font-medium text-amber-600">Intermittent</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span>Distribution points</span>
                          <span className="font-mono font-medium">2 identified</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-5">
                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2.5">Security Considerations</div>
                      <div className="flex items-center gap-2 p-3 bg-amber-100 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="text-[13px] font-medium text-amber-600">Moderate Risk</span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Avoid large gatherings. Recommend staggered distribution times and multiple smaller distribution points.
                      </p>
                    </div>

                    <div className="bg-gray-50 p-3">
                      <div className="text-[11px] text-gray-500 mb-1">Last Field Visit</div>
                      <div className="text-sm font-semibold">December 12, 2024</div>
                    </div>
                  </>
                )}
              </div>
            </WireframeCard>
          </div>
        </div>

        {/* Data Sources */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-gray-400" />
            <span className="text-[13px] font-semibold text-gray-900">Data Sources</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <WireframeCard noPadding>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-[13px] text-gray-900">Rapid Assessment</div>
                    <div className="text-[11px] text-gray-500">NRC/UNHCR</div>
                    <div className="text-[11px] text-gray-500">10/21/2025</div>
                  </div>
                  <span className="text-[13px] font-semibold font-mono text-gray-900">68%</span>
                </div>
                <div className="h-1 bg-gray-200">
                  <div className="h-full w-[68%] bg-accent-500" />
                </div>
              </div>
            </WireframeCard>
            <WireframeCard noPadding>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-[13px] text-gray-900">Census Projection</div>
                    <div className="text-[11px] text-gray-500">National Statistics</div>
                    <div className="text-[11px] text-gray-500">2024 Baseline</div>
                  </div>
                  <span className="text-[13px] font-semibold font-mono text-green-600">100%</span>
                </div>
                <div className="h-1 bg-gray-200">
                  <div className="h-full w-full bg-green-600" />
                </div>
              </div>
            </WireframeCard>
          </div>
        </div>
      </WireframeContent>

      {/* New Distribution Wizard Modal */}
      <NewDistributionWizard
        open={showDistributionWizard}
        onClose={() => setShowDistributionWizard(false)}
        locations={locationData}
        onComplete={handleDistributionComplete}
      />
    </WireframeLayout>
  );
}

function ProgressBar({ label, value, total }: { label: string; value: number; total: number }) {
  const percentage = Math.round((value / total) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="font-mono">{value.toLocaleString()} ({percentage}%)</span>
      </div>
      <div className="h-1.5 bg-gray-200">
        <div className="h-full bg-gray-500" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export default WireframeCashAssistance;
